import React, { useState } from 'react';
import { X, Sparkles, Loader2 } from 'lucide-react';

interface AIImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (imageData: {
    filename: string;
    mimeType: string;
    size: number;
    data: string;
    source: 'ai';
    metadata: any;
  }) => void;
  title?: string;
}

const AIImageModal: React.FC<AIImageModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  title = ''
}) => {
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Generate default prompt when modal opens
  React.useEffect(() => {
    if (isOpen && title) {
      const defaultPrompt = `Genera una imagen realista, horizontal y profesional que represente visualmente el concepto de "${title}". La imagen debe ser de alta calidad, con colores vibrantes pero profesionales, que ilustre de manera clara y atractiva el tema de la estadística o encuesta. Incluye elementos visuales que sugieran datos, análisis o investigación, manteniendo un estilo moderno y periodístico.`;
      setPrompt(defaultPrompt);
    }
  }, [isOpen, title]);

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    
    setGenerating(true);
    setError(null);
    setPreviewUrl(null);
    
    try {
      // TODO: Replace with actual ChatGPT API call
      const response = await fetch('/api/images/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: prompt.trim(),
          title: title
        })
      });
      
      if (!response.ok) {
        throw new Error('Error al generar la imagen');
      }
      
      const data = await response.json();
      
      if (data.error) {
        // Handle specific errors
        switch (data.error) {
          case 'OPENAI_NOT_CONFIGURED':
            setError('OpenAI no está configurado. Contacta al administrador.');
            break;
          case 'INVALID_API_KEY':
            setError('Clave de API de OpenAI inválida.');
            break;
          case 'RATE_LIMIT':
            setError('Límite de velocidad excedido. Intenta de nuevo en unos minutos.');
            break;
          case 'CONTENT_POLICY':
            setError('El prompt viola las políticas de contenido. Intenta con un prompt diferente.');
            break;
          default:
            setError(data.message || 'Error al generar la imagen');
        }
        return;
      }
      
      // Show the generated image preview
      if (data.url) {
        setPreviewUrl(data.url);
      }
      
      // Simulate successful generation
      setTimeout(() => {
        const imageData = {
          filename: data.filename || `ai-generated-${Date.now()}.png`,
          mimeType: data.mimeType || 'image/png',
          size: data.size || 1024,
          data: data.data || 'mock-base64-data',
          source: 'ai' as const,
          metadata: {
            prompt: prompt.trim(),
            model: data.metadata?.model || 'DALL-E 3',
            width: data.metadata?.width || 1792,
            height: data.metadata?.height || 1024,
            alt: `Imagen generada por IA: ${prompt.trim()}`
          }
        };
        
        onGenerate(imageData);
        setGenerating(false);
        onClose();
      }, 2000);
      
    } catch (err) {
      setError('Error al generar la imagen con IA');
      setGenerating(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !generating) {
      handleGenerate();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background border border-border rounded-lg p-6 w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Generar Imagen con IA</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground hover:bg-surface-200 rounded transition-colors"
            disabled={generating}
          >
            <X size={20} />
          </button>
        </div>

        {/* Prompt Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">
            Descripción de la imagen
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Describe la imagen que quieres generar..."
            className="w-full p-3 border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={4}
            disabled={generating}
          />
          <p className="text-xs text-muted-foreground mt-2">
            La imagen será horizontal, de alta calidad y optimizada para uso web.
          </p>
        </div>

        {/* Preview Area */}
        {previewUrl && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Vista previa
            </label>
            <div className="relative bg-surface-100 rounded-lg overflow-hidden">
              <img
                src={previewUrl}
                alt="Vista previa de imagen generada"
                className="w-full h-64 object-cover"
              />
              <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                Generando...
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-surface-200 transition-colors"
            disabled={generating}
          >
            Cancelar
          </button>
          <button
            onClick={handleGenerate}
            disabled={!prompt.trim() || generating}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {generating ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Generar Imagen
              </>
            )}
          </button>
        </div>

        {/* Footer Note */}
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            La generación de imágenes puede tomar entre 10-30 segundos. 
            Las imágenes generadas son únicas y libres de derechos de autor.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIImageModal; 